import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isObjectLike from 'lodash/isObjectLike'

import type { Dayjs } from 'configuration/dayjs'
import {
    KEYWORDS,
    CAMPAIGNS,
    PRODUCT_ASINS,
    COUNTRIES,
    PRODUCT_ADS,
    BRANDS,
    PORTFOLIOS,
    PARENT_ASINS,
    AD_GROUPS,
    DSP_ADVERTISERS,
    DSP_ORDERS,
    DSP_LINE_ITEMS,
    SEGMENTS,
    KEYWORD_RESEARCH_LIST,
} from 'const/filters'
import {
    REPORT_DATE_HOUR_ID,
    REPORT_DATE_DAY_ID,
    MAX_DAYS_RANGE_FOR_HOURLY_DATA,
} from 'const/widgets'
import { getDateRangeInDays } from 'helpers/dateRange'
import { PageId } from 'routing/pages'
import { Widget } from 'types'

interface FilterOverrides {
    mergeWithFilters?: boolean
    filters: Partial<Record<string, any>>
}

export function getFilterOverridesForPage(
    pageName: PageId,
    resource: any
): FilterOverrides {
    const { id } = resource
    if (pageName === 'keywordPage') {
        return {
            filters: {
                [KEYWORDS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'campaignPage') {
        return {
            filters: {
                [CAMPAIGNS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'productAdPage') {
        return {
            filters: {
                [PRODUCT_ADS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'adAccountPage') {
        return {
            filters: {
                [BRANDS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'productPage') {
        const { asin, marketplace, is_parent } = resource
        if (is_parent) {
            return {
                filters: {
                    [PARENT_ASINS]: [{ value: asin }],
                    [COUNTRIES]: [{ value: marketplace }],
                },
            }
        }
        return {
            filters: {
                [PRODUCT_ASINS]: [{ value: asin }],
                [COUNTRIES]: [{ value: marketplace }],
            },
        }
    }

    if (pageName === 'productDetailPage') {
        const { asin, marketplace } = resource
        return {
            mergeWithFilters: true,
            filters: {
                [PRODUCT_ASINS]: [{ value: asin }],
                [COUNTRIES]: [{ value: marketplace }],
            },
        }
    }

    if (pageName === 'portfolioPage') {
        return {
            filters: {
                [PORTFOLIOS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'adGroupPage') {
        return {
            filters: {
                [AD_GROUPS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'dspAdvertiserPage') {
        return {
            filters: {
                [DSP_ADVERTISERS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'dspOrderPage') {
        return {
            filters: {
                [DSP_ORDERS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'dspLineItemPage') {
        return {
            filters: {
                [DSP_LINE_ITEMS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'segmentPage') {
        return {
            mergeWithFilters: true,
            filters: {
                [SEGMENTS]: [{ value: id }],
            },
        }
    }

    if (pageName === 'keywordResearchListDetailPage') {
        return {
            mergeWithFilters: true,
            filters: {
                [KEYWORD_RESEARCH_LIST]: [{ value: id }],
            },
        }
    }

    if (pageName === 'categoryPerformancePage') {
        return {
            filters: {},
        }
    }

    return { filters: {} }
}

export function transformToString(config: any): string[] {
    if (!isEmpty(config) && isObjectLike(config[0])) {
        return config
            .filter((each: any) => !isNil(each))
            .map((each: any) => each.id)
    }

    return config
}

export const isWidgetHourlyAgg = (widget: Widget): boolean => {
    const widgetGroupBy = transformToString(widget.group_by)
    return !!widgetGroupBy?.length && widgetGroupBy[0] === REPORT_DATE_HOUR_ID
}

/**
 * takes a widget and a dates range. If the widget is supposed to be hourly aggregated but the date range is too big, then it zooms out the widget to work on a daily aggregation.
 *
 * @param widget The original widget (won't be changed)
 * @param dateRange string[] | Dayjs[]
 *
 * @returns an Object
 * {
 *    widget: the new widget (possibly zoomed out),
 *   isZoomedOut: a boolean saying whether the widget was zoomed out or not.
 * }
 */
export const zoomOut = (
    widget: Widget,
    dateRange?: string[] | Dayjs[]
): { isZoomedOut: boolean; widget: Widget } => {
    const dateRangeInDays =
        dateRange?.length === 2
            ? getDateRangeInDays(dateRange[0], dateRange[1])
            : 0
    const hasHourlyData = isWidgetHourlyAgg(widget)
    const tempWidget = cloneDeep(widget)
    let isZoomedOut = false
    if (hasHourlyData && dateRangeInDays > MAX_DAYS_RANGE_FOR_HOURLY_DATA) {
        tempWidget.sorter = { ...widget.sorter, field: REPORT_DATE_DAY_ID }
        tempWidget.group_by = widget.group_by.map((grouped, idx) =>
            idx === 0 ? { ...grouped, id: REPORT_DATE_DAY_ID } : grouped
        )
        tempWidget.order_by = REPORT_DATE_DAY_ID
        isZoomedOut = true
    }
    return {
        isZoomedOut,
        widget: tempWidget,
    }
}
